<template>
  <div data-testid="notification" class="notification">
    <div class="icon-wrapper">
      <svg
        v-if="!notification.type || notification.type === 'info'"
        width="24"
        height="24"
        class="info-icon"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <svg
        v-else-if="notification.type === 'success'"
        width="24"
        height="24"
        class="success-icon"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <svg
        v-else-if="notification.type === 'error'"
        width="24"
        height="24"
        class="error-icon"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clip-rule="evenodd"
        />
      </svg>
    </div>
    <div class="content-wrapper">
      <p class="title">{{ notification.title }}</p>
      <p class="text" :class="{ 'text-margin': notification.title }">
        {{ notification.text }}
      </p>
    </div>
    <div class="close-icon-wrapper">
      <svg
        class="close-icon"
        data-testid="close-notification"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="currentColor"
        @click="removeNotification"
      >
        <path
          data-testid="close-notification-path"
          fill-rule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { PropType, ref, onMounted, onBeforeUnmount } from "vue";
  import INotification from "types/Notification/INotification";
  import { useNotificationsStore } from "../../../../stores/notifications";

  const props = defineProps({
    notification: {
      type: Object as PropType<INotification>,
      required: true,
    },
  });

  const notificationStore = useNotificationsStore();

  const timeout = ref();

  function removeNotification() {
    notificationStore.removeNotification(props.notification);
  }

  onMounted(() => {
    //Compatiblity fix because of typo persistant -> persistent
    if (
      props.notification.persistent !== true &&
      //@ts-expect-error
      props.notification.persistant !== true
    ) {
      timeout.value = setTimeout(() => removeNotification(), 3000);
    }
  });

  onBeforeUnmount(() => {
    clearTimeout(timeout.value);
  });
</script>

<style scoped lang="scss">
  @import "../../../../css/helpers";

  .notification {
    padding: 1rem;
    min-width: 300px;
    border-radius: var(--b-radius-large);
    margin-top: 12px;
    display: flex;
    align-items: flex-start;
    border: 1px solid #ebedf0;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.01);
    background-color: white;
  }

  .title {
    color: #161e2e;
    @include font(
      --chip-font,
      var(--font-medium),
      var(--font-sm),
      1,
      var(--font-family-primary)
    );
  }

  .text {
    color: #6b7280;
    margin-top: 0.095rem;
    @include font(
      --chip-font,
      var(--font-normal),
      var(--font-xs),
      1,
      var(--font-family-primary)
    );

    &.text-margin {
      margin-top: 0.35rem;
    }
  }

  .success-icon {
    color: #31c48d;
  }

  .info-icon {
    color: #1899d4;
  }

  .error-icon {
    color: #ee412a;
  }

  .close-icon {
    color: #9fa6b2;
    cursor: pointer;

    &:hover {
      transition: $hover-transition;
      color: #777b83;
    }
  }

  .icon-wrapper {
    flex-shrink: 0;
  }

  .content-wrapper {
    flex: 1 1 0%;
    margin-left: 8px;
    padding-top: 4px;
  }

  .close-icon-wrapper {
    flex-shrink: 0;
    margin-left: 1rem;
  }
</style>
